import { useMemo, useCallback } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
// components
import {
  MiniDrawer,
  SayIcon,
  getAuthToken,
  removeAuthToken,
} from "@asayinc/component-library";
import {
  SettingsOutlined,
  Settings,
  InsertInvitationOutlined,
  InsertInvitation,
  EmailOutlined,
  Email,
  HelpOutlineOutlined,
  PeopleAlt,
  PeopleAltOutlined,
  HowToVote,
  HowToVoteOutlined,
} from "@mui/icons-material";
import { DotIndicator, IconWithDot, SideNavItem } from "./components";
// redux / data
import {
  useGetFeaturesQuery,
  useGetSettingsQuery,
} from "../../../../store/settings";
import { useFeaturesDisabled } from "../../../../hooks/useFeaturesDisabled";
import {
  useGetUserInfoQuery,
  useTrackEventMutation,
} from "../../../../store/consumerUser";
import { useGetAppConfigQuery } from "../../../../store/appConfig";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/dist/query";
// utils
import { track } from "../../../../analytics";
import { jwtDecode } from "jwt-decode";
// types / constants
import {
  UiEventActions,
  UiEventTypes,
} from "../../../../store/consumerUser/types";
import { IUser, IUserFeatures } from "../../../../store/consumerUser/types";
import { IFeatures } from "../../../../store/settings/types";
import { IAppConfig } from "../../../../store/appConfig/types";

interface Token {
  first_name: string;
  last_name: string;
  email: string;
}

const pathnameTrackingActions = {
  "/shareholders": UiEventActions.TabClickShareholderNetwork,
  "/network": UiEventActions.TabClickShareholderNetwork, // Reusing the same action for now
  "/messages": UiEventActions.TabClickMessages,
  "/vote": UiEventActions.TabClickVoteMessages,
};

const SideNav = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();

  const authToken = getAuthToken();
  const decodedToken = useMemo(() => {
    if (!authToken) return null;
    try {
      return jwtDecode<Token>(authToken);
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [authToken]);

  const { email, first_name } = decodedToken || {};
  const { data, isLoading, isError, error } = useGetSettingsQuery(
    decodedToken ? undefined : skipToken
  );
  const { isSingleFeature } = useFeaturesDisabled();
  const { data: featuresData = {} } = useGetFeaturesQuery(
    decodedToken ? undefined : skipToken
  );
  const { data: userData = {} } = useGetUserInfoQuery(
    decodedToken ? undefined : skipToken
  );
  const { data: appConfigData = {} } = useGetAppConfigQuery(
    decodedToken ? undefined : skipToken
  );
  const { messages, shareholders, voteSolicitation } =
    featuresData as IFeatures;
  const { features: flags = {} } = userData as IUser;
  const { portalMessagesTabUpsell, portalSnTabUpsell, portalVsTabUpsell } =
    flags as IUserFeatures;
  const { useNewNetworkPage = false } = appConfigData as IAppConfig;

  const [trackEvent] = useTrackEventMutation();
  const itemAction = (pathname: string) => {
    const trackingAction = pathnameTrackingActions[pathname];
    // if there's a tracking action for the tab make tracking request
    if (trackingAction) {
      trackEvent({
        action: trackingAction,
        type: UiEventTypes.Click,
        sourceUrl: window.location.href,
        targetUrl: `${window.location.origin}${pathname}`,
      });
    }

    if (pathname === "mail") {
      window.open(
        `mailto:support@saytechnologies.com?subject=${companyName} - Issuer Portal Support`
      );
    } else {
      navigate(pathname);
    }
  };

  const allItems = [
    {
      value: "/",
      active: location.pathname === "/",
      icon: <SayIcon name="HomeOutline" />,
      activeIcon: <SayIcon name="HomeFilled" />,
      text: <SideNavItem label="Home" showPill={false} />,
    },
    {
      value: "/shareholders",
      active: location.pathname.startsWith("/shareholders"),
      icon: (
        <IconWithDot
          icon={<PeopleAltOutlined />}
          showDot={portalSnTabUpsell && !shareholders && !isSingleFeature}
        />
      ),
      activeIcon: <PeopleAlt />,
      text: (
        <SideNavItem
          label="Shareholders"
          showPill={isSingleFeature && portalSnTabUpsell && !shareholders}
        />
      ),
      showWhen: !useNewNetworkPage,
    },
    {
      value: "/network",
      active: location.pathname.startsWith("/network"),
      icon: <PeopleAltOutlined />,
      activeIcon: <PeopleAlt />,
      text: <SideNavItem label="Network" showPill={false} />,
      showWhen: useNewNetworkPage,
    },
    {
      value: "/messages",
      active: location.pathname.startsWith("/messages"),
      icon: (
        <IconWithDot
          icon={<EmailOutlined />}
          showDot={portalMessagesTabUpsell && !messages && !isSingleFeature}
        />
      ),
      activeIcon: <Email />,
      text: (
        <SideNavItem
          label="Messages"
          showPill={isSingleFeature && portalMessagesTabUpsell && !messages}
        />
      ),
    },
    {
      value: "/qa",
      active: location.pathname.startsWith("/qa"),
      icon: <InsertInvitationOutlined />,
      activeIcon: <InsertInvitation />,
      text: "Q&A",
    },
    {
      value: "/vote",
      active: location.pathname.startsWith("/vote"),
      icon: (
        <IconWithDot
          icon={<HowToVoteOutlined />}
          showDot={portalVsTabUpsell && !voteSolicitation && !isSingleFeature}
        />
      ),
      activeIcon: <HowToVote />,
      text: (
        <SideNavItem
          label="Voting"
          showPill={isSingleFeature && portalVsTabUpsell && !voteSolicitation}
        />
      ),
    },
  ];

  // Filter items based on feature flags
  const items = allItems.filter(
    (item) => !item.hasOwnProperty("showWhen") || item.showWhen
  );

  const footerItems = [
    {
      value: "/settings",
      active: location.pathname === "/settings",
      icon: <SettingsOutlined />,
      activeIcon: <Settings />,
      text: "Settings",
      node: !isLoading && !data?.completed ? <DotIndicator /> : undefined,
    },
    {
      value: "mail",
      active: false,
      icon: <HelpOutlineOutlined />,
      text: "Support",
    },
  ];

  const companyName = data?.name || "Unknown Company";

  // use root page logic to navigate user on logo click
  const onLogoClick = useCallback(() => {
    navigate("/");
  }, []);

  const logOutClick = () => {
    track({ name: "Logout Pressed" });
    window.localStorage.clear();
    removeAuthToken();
    navigate("/login");
  };

  let profileProps;
  if (decodedToken) {
    profileProps = {
      logoutAction: logOutClick,
      name: first_name || "",
      email: email || "",
      companyLogo: data?.logo,
      companyTicker: data?.securities?.[0]?.symbol,
      companyName,
    };
  }

  // if this api fails with 403 or 401 user is unauthorized
  if (isError && error) {
    if (
      (error as FetchBaseQueryError).status === 403 ||
      (error as FetchBaseQueryError).status === 401
    ) {
      removeAuthToken();
      return <Navigate to={"/login"} replace />;
    }
  }

  return (
    <MiniDrawer
      showSayLogo
      defaultExpanded
      onClickItem={itemAction}
      onLogoClick={onLogoClick}
      sx={{ height: "100vh", position: "sticky", top: "0" }}
      listActiveBg
      items={items}
      footerItems={footerItems}
      profileProps={profileProps}
    />
  );
};

export default SideNav;
